import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import clientsManifest from '../data/ourClientsManifest.json';
import { SITE_TRUST_STATS } from '../data/siteTrustStats';
import './OurClientsSection.css';

/** CRA `homepage` / CDN subpath support */
function publicUrl(path) {
  const base = (process.env.PUBLIC_URL || '').replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

function logosFromManifest(data) {
  const raw = data?.logos;
  if (!Array.isArray(raw)) return [];
  return raw
    .map((entry) => {
      if (!entry?.file) return null;
      const file = String(entry.file).replace(/^\/+/, '');
      const src = publicUrl(`/our-clients/${file}`);
      const alt = entry.alt && String(entry.alt).trim() ? String(entry.alt).trim() : 'Trusted client in Ahmedabad';
      return { src, alt };
    })
    .filter(Boolean);
}

/**
 * Infinite marquee: duplicate logo list so translateX(-50%) loops seamlessly.
 */
function OurClientsSection() {
  const logos = useMemo(() => logosFromManifest(clientsManifest), []);

  const marqueeTrack = useMemo(() => {
    if (!logos.length) return [];
    return [...logos, ...logos];
  }, [logos]);

  return (
    <section className="our-clients-section animate-on-scroll" aria-labelledby="our-clients-heading">
      <div className="our-clients-inner">
        <div className="container our-clients-header">
          <h2 id="our-clients-heading" className="our-clients-title">
            Trusted by Leading Clients in Ahmedabad
          </h2>
          <p className="our-clients-subtitle">
            We proudly serve corporate offices, hotels, hospitals, and residential societies across Ahmedabad with
            reliable valet parking, manpower, housekeeping, and cleaning services.
          </p>

          <ul className="our-clients-stats" aria-label="Company highlights">
            {SITE_TRUST_STATS.map((s) => (
              <li key={s.label}>
                <span className="our-clients-stat-value">{s.value}</span>
                <span className="our-clients-stat-label">{s.label}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {logos.length > 0 && (
        <div className="our-clients-marquee-fullbleed" aria-label="Client logos, auto-scrolling">
          <div className="our-clients-marquee-fade" />
          <div className="our-clients-marquee-viewport">
            <div className="our-clients-marquee-track">
              {marqueeTrack.map((logo, i) => (
                <div className="our-clients-marquee-cell" key={`${logo.src}-${i}`}>
                  <img
                    src={logo.src}
                    alt={logo.alt}
                    className="our-clients-logo-img"
                    loading="lazy"
                    decoding="async"
                    width={180}
                    height={90}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="our-clients-inner">
        <div className="container our-clients-header our-clients-footer-copy">
          <p className="our-clients-seo">
            From SG Highway to Satellite and Bopal, businesses choose {` `}
            <Link to="/facility-management-ahmedabad">facility management in Ahmedabad</Link>
            {` `} that covers staffing, soft services, and day-to-day discipline. Venues and corporates pair {` `}
            <Link to="/valet-parking-ahmedabad">valet parking in Ahmedabad</Link>
            {` `} with lobby and parking support, while HR teams scale operations using {` `}
            <Link to="/manpower-services-ahmedabad">manpower services in Ahmedabad</Link>
            {` `} for office boys, peons, helpers, and housekeeping—one accountable partner instead of many ad-hoc
            vendors.
          </p>
        </div>
      </div>
    </section>
  );
}

export default OurClientsSection;
